import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * MobileBottomSheet - Bottom sheet modal for mobile
 * Slides up from bottom on mobile/tablet, falls back to fixed modal on desktop (≥1024px)
 * Fixed height - no snap points or expansion allowed
 *
 * Props:
 * - isOpen: boolean - Whether bottom sheet is open
 * - onClose: function - Called when closing
 * - title: string - Sheet title
 * - children: ReactNode - Sheet content
 * - snapPoints: array (default [0.6]) - Fixed snap height (0-1, where 1 is full height)
 * - initialSnap: number (default 0.6) - Initial sheet height (60% of viewport)
 * - allowDismiss: boolean (default true) - Allow swiping down to close
 * - showHandle: boolean (default true) - Show drag handle
 * - darkMode: boolean (default false) - Use dark theme
 */
const MobileBottomSheet = ({
  isOpen = false,
  onClose = () => {},
  title = '',
  children = null,
  initialSnap = 0.6,
  allowDismiss = true,
  showHandle = true,
  darkMode = false,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const [currentSnap, setCurrentSnap] = useState(initialSnap);
  const sheetRef = useRef(null);
  const contentRef = useRef(null);

  // Color classes based on theme
  const bgClass = darkMode ? 'bg-slate-900' : 'bg-white';
  const borderClass = darkMode ? 'border-white/10' : 'border-gray-200';
  const textClass = darkMode ? 'text-white' : 'text-gray-900';
  const mutedTextClass = darkMode ? 'text-white/60' : 'text-gray-600';
  const hoverBgClass = darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100';
  const handleColor = darkMode ? 'bg-white/20' : 'bg-gray-300';

  const handleClose = useCallback(() => {
    setTimeout(() => {
      onClose();
      setCurrentSnap(initialSnap);
    }, 300);
  }, [onClose, initialSnap]);

  // Add swipe down listener to sheet
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !isOpen) return;

    const handleTouchStart = (e) => {
      sheet.touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      if (sheet.touchStartY === undefined) return;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchEndY - sheet.touchStartY;

      // If swiped down more than 50px, close
      if (deltaY > 50 && allowDismiss) {
        handleClose();
      }
      sheet.touchStartY = undefined;
    };

    sheet.addEventListener('touchstart', handleTouchStart, false);
    sheet.addEventListener('touchend', handleTouchEnd, false);

    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart);
      sheet.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, allowDismiss, handleClose]);

  // Mobile bottom sheet (on mobile and tablet - screens < 1024px)
  if (isMobile || isTablet) {
    // Only render if isOpen is true
    if (!isOpen) {
      return null;
    }

    // Render using createPortal to escape relative positioned parents
    return createPortal(
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 opacity-100`}
          onClick={handleClose}
        />

        {/* Bottom Sheet */}
        <div
          ref={sheetRef}
          className={`fixed left-0 right-0 z-50 ${bgClass} rounded-t-2xl shadow-2xl transition-all duration-300 ease-out translate-y-0`}
          style={{
            bottom: '80px',
            maxHeight: `${currentSnap * window.innerHeight}px`,
            height: `${currentSnap * window.innerHeight}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with drag handle */}
          {showHandle && (
            <div className="flex justify-center pt-3 pb-2">
              <div className={`w-12 h-1 ${handleColor} rounded-full`} />
            </div>
          )}

          {/* Title */}
          {title && (
            <div className={`flex items-center justify-between px-4 py-3 border-b ${borderClass}`}>
              <h2 className={`text-lg font-semibold ${textClass}`}>{title}</h2>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className={`p-2 ${hoverBgClass} rounded-lg transition-colors`}
                aria-label="Cerrar"
              >
                <X size={20} className={mutedTextClass} />
              </button>
            </div>
          )}

          {/* Content */}
          <div
            ref={contentRef}
            className="flex-1 overflow-y-auto px-4 py-4"
            data-testid="bottom-sheet-content"
          >
            {children}
          </div>

        </div>
      </>,
      document.body
    );
  }

  // Desktop modal (fixed positioning)
  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={handleClose}
        />
      )}

      <div
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${bgClass} rounded-xl shadow-2xl z-50 max-w-md w-full mx-4 transition-all duration-300 ${
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        {/* Desktop modal header */}
        {title && (
          <div className={`flex items-center justify-between px-6 py-4 border-b ${borderClass}`}>
            <h2 className={`text-lg font-semibold ${textClass}`}>{title}</h2>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className={`p-2 ${hoverBgClass} rounded-lg transition-colors`}
              aria-label="Cerrar"
            >
              <X size={20} className={mutedTextClass} />
            </button>
          </div>
        )}

        {/* Desktop modal content */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </>
  );
};

export default MobileBottomSheet;
